/**
 * Who is signed in, in cookie mode — and what that lets them do.
 *
 * `GET /api/session` is the SPA's first call against the BFF: it is the only way this app
 * can learn its own identity, because the session cookie is HttpOnly and deliberately
 * unreadable. The answer carries the CSRF token as well, which the BFF also writes as the
 * readable `kkp_csrf` companion cookie that `auth.ts` reads.
 *
 * PASSWORD MODE HAS NO SESSION. `null` therefore means "no BFF", not "signed out", and the
 * role predicates below answer `true` for it — the shared POC password opens everything the
 * decision API exposes, exactly as before the cutover. Only cookie mode has roles to check.
 */

/** Mirrors `AuthEndpoints.Describe` in dotnet/admin-api. */
export interface SessionUser {
  id: string;
  upn: string;
  displayName: string;
  /** `upn (id)` — the exact string the BFF writes as `actor` upstream. */
  actor: string;
  /** The sign-in name, lower-cased — the user store's key (R8). Absent from an older BFF. */
  username?: string;
}

/** The body of `POST /auth/login` and `GET /api/session`. */
export interface Session {
  user: SessionUser;
  roles: string[];
  csrfToken: string;
  /**
   * `GET /api/session` says `true` beside a live session. Signed out it answers
   * `200 {"authenticated":false}` instead of a 401 (an older BFF answered 401) — `api.session`
   * turns either into `null`, so a `Session` object always means somebody is signed in.
   */
  authenticated?: boolean;
  /**
   * True while the user holds a temporary password (a new user, or after an Admin reset it).
   * The BFF refuses every other route with 403 `password_change_required` until they change
   * it, so the app shows the change-password screen instead of the console (R8).
   */
  mustChangePassword?: boolean;
}

/** `Admin` manages users — and, since R8b, every rule set — and nothing else; roles are additive (R8). */
export type Role = 'Viewer' | 'Editor' | 'Approver' | 'Admin';

export function hasRole(session: Session | null, role: Role): boolean {
  // No session object at all = password mode, which has no role model.
  if (session === null) return true;
  return session.roles.some((r) => r.toLowerCase() === role.toLowerCase());
}

/** May raise, validate and replay a proposal. (Proposal flow — unused while maker-checker is dropped.) */
export const canPropose = (s: Session | null): boolean => hasRole(s, 'Editor');

/** May approve, publish and roll back a proposal. (Proposal flow — unused while maker-checker is dropped.) */
export const canApprove = (s: Session | null): boolean => hasRole(s, 'Approver');

/**
 * May publish the draft (`PUT /api/rules`) and roll back (`…/activate`): Editor or Approver,
 * mirroring the BFF's `CanPublish` policy. A Viewer never. Password mode (no session) may.
 */
export const canPublish = (s: Session | null): boolean => canPropose(s) || canApprove(s);

/**
 * May change the draft — add, edit, delete, switch on/off, replace or restore a rule, and the
 * rule set's settings: Editor or Approver. The same two roles as `canPublish`, named for what
 * the screens gate on. A Viewer only reads. Password mode (no session) may.
 */
export const canEdit = (s: Session | null): boolean => canPropose(s) || canApprove(s);

/** Read-only: signed in, but neither maker nor checker. */
export const isViewerOnly = (s: Session | null): boolean => s !== null && !canPropose(s) && !canApprove(s);

/**
 * May manage console users (`/api/users`, R8): Admin only, mirroring the BFF's `CanManageUsers`.
 * Unlike the rule predicates above, password mode (no session) may NOT — there is no BFF, so
 * there are no users to manage and the Users entry stays out of the navigation.
 */
export const canManageUsers = (s: Session | null): boolean => s !== null && hasRole(s, 'Admin');

/**
 * Is this session the person who raised the proposal? The BFF does not publish the author's
 * user id, but it publishes the author's `actor`, which is derived from the session the same
 * way on both sides — so comparing the two is comparing the same server-minted string.
 *
 * This is only ever a UI courtesy. `POST /api/proposals/{id}/approve` enforces maker ≠ checker
 * server-side and answers 403 `maker_is_checker` whatever the browser believes.
 */
export function isAuthor(session: Session | null, createdBy: string | undefined): boolean {
  if (session === null || !createdBy) return false;
  return session.user.actor === createdBy;
}

// ---- R8b per-rule-set permissions -----------------------------------------------

/**
 * May create a rule set, make one the primary, archive or restore one: Admin only (R8b — it was
 * Editor or Approver before), mirroring the BFF's `CanManageRuleSets`. Password mode has no
 * registry to write.
 */
export const canManageRuleSets = (s: Session | null): boolean => s !== null && hasRole(s, 'Admin');

/**
 * The per-set gate the screens use: the set's `access` from `GET /api/rulesets` when the admin
 * service sent one; otherwise (password mode, an older service, the registry not loaded yet) the
 * global role, as before R8b — where an Admin now counts as a writer. The BFF enforces either way.
 */
export function mayOnSet(s: Session | null, access: 'view' | 'edit' | 'publish' | 'none' | undefined, needs: 'view' | 'edit' | 'publish'): boolean {
  if (access !== undefined) {
    const rank = { none: 0, view: 1, edit: 2, publish: 3 } as const;
    return rank[access] >= rank[needs];
  }
  if (needs === 'view') return true;
  return needs === 'edit' ? canEdit(s) || hasRole(s, 'Admin') : canPublish(s) || hasRole(s, 'Admin');
}
